import { useState } from 'react';
import { BellAlertIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

import axiosClient from '../../api/axios-client';

import Button from '../components/Button';
import Text from '../components/Text';
import { useAbonnementPush } from '../api/useAbonnementPush';
import { t } from '../i18n/langue';

import { TexteRiche } from './sas/briques';

/**
 * L'amorce des notifications — #21, #79.
 *
 * ## La boîte système n'est JAMAIS présentée à froid
 *
 * C'est la seule raison d'être de cet écran, et elle est irrattrapable : sur iOS,
 * `requestPermission()` ne pose la question qu'**une seule fois**. Un « Ne pas
 * autoriser » rend `denied` à jamais, et le seul rattrapage est Réglages iOS —
 * une app web ne peut ni les ouvrir ni redemander.
 *
 * Présenter la boîte au chargement, c'est donc jouer la valeur entière du produit
 * sur un tap réflexe. L'écran explique d'abord ; la boîte ne s'ouvre qu'à
 * quelqu'un qui vient de dire oui.
 *
 * ## « Plus tard » est un vrai choix
 *
 * Discret, mais présent et non bloquant. L'amorce ne se represente pas au
 * démarrage suivant : quelqu'un qui a dit « plus tard » l'a dit, et le harceler à
 * chaque ouverture est le meilleur moyen d'obtenir le « Ne pas autoriser » qu'on
 * cherche justement à éviter. #80 lui rendra la main quand il le voudra.
 */

/** Le tap qui referme l'amorce, pour ne pas la reposer à chaque ouverture. */
export const CLE_AMORCE_VUE = 'MOBILE_AMORCE_VUE';

type Etape = 'amorce' | 'envoi' | 'reussi' | 'refuse' | 'echec';

/**
 * Ce qui fera sonner le téléphone de ce compte — lot 3 Odomo. La messagerie prime
 * (voir `motifAmorce`) ; « menage » vise l'agent d'étage, prévenu quand des chambres
 * lui sont affectées pour aujourd'hui.
 */
export type MotifAmorce = 'messagerie' | 'menage' | 'maintenance';

const LIGNES: Record<MotifAmorce, string[]> = {
    messagerie: ['messageClient', 'botPasseLaMain'],
    menage: ['chambresAffectees'],
    maintenance: ['nouvellePanne', 'panneConfiee'],
};

function promesseDe(motif: MotifAmorce): { titre: string; raison: string; lignes: string[]; refus: string } {
    const cle = `notifications.amorce.${motif}`;

    return {
        titre: t(`${cle}.titre`),
        raison: t(`${cle}.raison`),
        lignes: LIGNES[motif].map((ligne) => t(`${cle}.${ligne}`)),
        refus: t(`${cle}.refus`),
    };
}

interface AmorceProps {
    /** Rendre la main à l'app : l'amorce est finie, quelle qu'en soit l'issue. */
    onTermine: () => void;
    /** Ce que la carte promet. La messagerie par défaut : c'est le cas historique. */
    motif?: MotifAmorce;
}

export default function AmorceNotifications({ onTermine, motif = 'messagerie' }: AmorceProps) {
    const promesse = promesseDe(motif);
    const { demanderEtAbonner } = useAbonnementPush();
    const [etape, setEtape] = useState<Etape>('amorce');

    const marquerVue = () => {
        try {
            localStorage.setItem(CLE_AMORCE_VUE, '1');
        } catch {
            /* Stockage refusé : l'amorce se reposera, c'est le moindre mal. */
        }
    };

    const activer = async () => {
        setEtape('envoi');

        let permission: Awaited<ReturnType<typeof demanderEtAbonner>>;
        try {
            permission = await demanderEtAbonner();
        } catch {
            // Réseau, clé VAPID absente, service worker indisponible : l'écran restait
            // figé sur « Activation… » (audit 2026-09-23). Pas de drapeau posé.
            setEtape('echec');
            return;
        }
        marquerVue();

        if (permission !== 'granted') {
            setEtape('refuse');
            return;
        }

        // La preuve part du serveur, sans qu'on la demande (#21). Son échec ne
        // remet pas en cause l'abonnement, qui lui a réussi : on affiche la
        // confirmation quand même, et l'écran d'état de #80 portera le diagnostic.
        await axiosClient.post('/web-push/welcome').catch(() => undefined);

        setEtape('reussi');
    };

    const plusTard = () => {
        marquerVue();
        onTermine();
    };

    if (etape === 'reussi') return <BienvenueEnvoyee onContinuer={onTermine} />;
    if (etape === 'refuse') return <PermissionRefusee consequence={promesse.refus} onContinuer={onTermine} />;

    return (
        <div className="tf-shell flex h-full flex-col overflow-y-auto bg-white">
            <main className="mx-auto flex min-h-full w-full max-w-md flex-col px-7 pt-10">
                <div className="flex justify-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-main-color-light">
                        <BellAlertIcon aria-hidden="true" className="h-10 w-10 text-main-color" />
                    </div>
                </div>

                <Text variante="titre" as="h1" className="mt-7 text-center">
                    {promesse.titre}
                </Text>

                <Text variante="petit" ton="attenue" className="mt-3 block text-center leading-snug">
                    {promesse.raison}
                </Text>

                <ul className="mt-7 flex flex-col gap-3">
                    {promesse.lignes.map(
                        (ligne) => (
                            <li key={ligne} className="flex items-start gap-2.5">
                                <CheckCircleIcon
                                    aria-hidden="true"
                                    className="mt-0.5 h-5 w-5 shrink-0 text-main-color"
                                />
                                <Text variante="petit">{ligne}</Text>
                            </li>
                        ),
                    )}
                </ul>

                {/* Dire ce qu'on n'enverra PAS vaut autant que dire ce qu'on enverra :
                    c'est la crainte qui fait refuser, pas l'ignorance. */}
                <Text variante="petit" ton="attenue" className="mt-3 block leading-snug">
                    {t('notifications.amorce.rienDAutre')}
                </Text>

                <div className="mt-7 rounded-xl bg-amber-50 px-3 py-2.5">
                    <Text variante="petit" className="leading-snug text-amber-900">
                        <TexteRiche texte={t('notifications.amorce.uneSeuleFois')} />
                    </Text>
                </div>

                {etape === 'echec' && (
                    <div role="alert" className="mt-4">
                        <Text variante="petit" ton="danger" className="block leading-snug">
                            {t('notifications.amorce.echec')}
                        </Text>
                    </div>
                )}

                <div className="mt-auto flex flex-col gap-2 pb-8 pt-7">
                    <Button
                        libelle={
                            etape === 'envoi'
                                ? t('notifications.amorce.activationEnCours')
                                : etape === 'echec'
                                  ? t('commun.reessayer')
                                  : t('notifications.activer')
                        }
                        onPress={() => void activer()}
                        disabled={etape === 'envoi'}
                        pleineLargeur
                    />
                    <Button
                        libelle={t('notifications.amorce.plusTard')}
                        onPress={plusTard}
                        variante="discret"
                        disabled={etape === 'envoi'}
                        pleineLargeur
                    />
                </div>
            </main>
        </div>
    );
}

/**
 * La confirmation — et l'invitation à vérifier soi-même.
 *
 * « Verrouillez l'écran » n'est pas une coquetterie : c'est ce qui transforme une
 * affirmation du produit en constat de l'utilisateur. Un bandeau qui arrive
 * pendant qu'on regarde l'app ne prouve rien de ce qui compte.
 */
function BienvenueEnvoyee({ onContinuer }: { onContinuer: () => void }) {
    return (
        <div className="tf-shell flex h-full flex-col items-center justify-center bg-white px-8 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-main-color-light">
                <CheckCircleIcon aria-hidden="true" className="h-11 w-11 text-main-color" />
            </div>

            <Text variante="titre" as="h1" className="mt-6">
                {t('notifications.amorce.cestActive')}
            </Text>

            <Text variante="petit" ton="attenue" className="mt-3 block leading-snug">
                {t('notifications.amorce.bienvenueEnvoyee')}
            </Text>

            <div className="mt-8 w-full max-w-xs">
                <Button libelle={t('notifications.amorce.continuer')} onPress={onContinuer} pleineLargeur />
            </div>
        </div>
    );
}

/**
 * Le refus, nommé plutôt que masqué.
 *
 * Sur iOS il est **définitif depuis l'app**, et le taire produirait exactement la
 * panne silencieuse que la carte combat : quelqu'un qui croit être joignable et ne
 * l'est pas. La consigne vers les Réglages est tout ce qu'on peut offrir — aucune
 * API web ne les ouvre.
 */
function PermissionRefusee({ consequence, onContinuer }: { consequence: string; onContinuer: () => void }) {
    return (
        <div className="tf-shell flex h-full flex-col items-center justify-center bg-white px-8 text-center">
            <Text variante="titre" as="h1">
                {t('notifications.amorce.desactivees')}
            </Text>

            <Text variante="petit" ton="attenue" className="mt-3 block leading-snug">
                {t('notifications.amorce.refusDetail', { consequence })}
            </Text>

            <div className="mt-8 w-full max-w-xs">
                <Button libelle={t('notifications.amorce.continuer')} onPress={onContinuer} pleineLargeur />
            </div>
        </div>
    );
}
